import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, TextField, MenuItem } from "@mui/material";
import Grid from "./utils/CompatGrid";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import TopToolbar from "./components/TopToolbar";
import LeftPanel from "./components/LeftPanel";
import RightPanel from "./components/RightPanel";
import DesignerCanvasElement from "./components/DesignerCanvasElement";
import LabelCanvas from "./components/LabelCanvas";
import {
  createElement,
  createDynamicFieldElement,
} from "./utils/elementFactory";
import { mmToPx, PAPER_TYPES } from "./utils/labelSizes";
import SAMPLE_DATA_MAP from "./utils/sampleData";
import { exportSingleLabelPdf, printLabelNode } from "./utils/pdfExport";
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  getTemplates,
  getProducts,
  resolvePlaceholders,
} from "./api/labelStudioApi";
import { normalizeNutritionRows } from "./utils/nutritionTable";
import {
  CUSTOM_BACKGROUND_OPTION,
  findThemeByKey,
  getThemeSelectionValue,
  LABEL_BACKGROUND_THEMES,
  NO_BACKGROUND_OPTION,
} from "./utils/backgroundThemes";

const DEFAULT_TEMPLATE_META = {
  widthMm: 60,
  heightMm: 40,
  paperType: "INDIVIDUAL_STICKER",
  backgroundColor: "#ffffff",
  backgroundImageUrl: "",
  backgroundImageOpacity: 0.35,
  borderColor: "#d0d7de",
  borderWidth: 0,
  a4Columns: 2,
  a4Rows: 5,
  a4MarginXMm: 5,
  a4MarginYMm: 5,
  a4SpacingXMm: 2,
  a4SpacingYMm: 2,
};

const toFiniteNumber = (value, fallback, { min = null, max = null } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  if (min != null && parsed < min) return min;
  if (max != null && parsed > max) return max;
  return parsed;
};

const sanitizeElement = (element, index) => {
  const safeType = typeof element?.type === "string" ? element.type : "text";
  const safeZIndex = toFiniteNumber(element?.zIndex, index + 1, { min: 0 });
  const defaults = createElement(safeType, safeZIndex);
  const nutritionRows =
    safeType === "nutrition"
      ? normalizeNutritionRows(element?.nutritionRows)
      : undefined;

  return {
    ...defaults,
    ...element,
    id:
      typeof element?.id === "string" && element.id.trim()
        ? element.id
        : defaults.id,
    type: defaults.type,
    zIndex: safeZIndex,
    x: toFiniteNumber(element?.x, defaults.x, { min: 0 }),
    y: toFiniteNumber(element?.y, defaults.y, { min: 0 }),
    width: toFiniteNumber(element?.width, defaults.width, { min: 1 }),
    height: toFiniteNumber(element?.height, defaults.height, { min: 1 }),
    rotation: toFiniteNumber(element?.rotation, defaults.rotation, {
      min: 0,
      max: 360,
    }),
    opacity: toFiniteNumber(element?.opacity, defaults.opacity, {
      min: 0,
      max: 1,
    }),
    borderWidth: toFiniteNumber(
      element?.borderWidth,
      defaults.borderWidth ?? 0,
      { min: 0 },
    ),
    borderRadius: toFiniteNumber(
      element?.borderRadius,
      defaults.borderRadius ?? 0,
      { min: 0 },
    ),
    padding: toFiniteNumber(element?.padding, defaults.padding ?? 0, {
      min: 0,
    }),
    fontSize: toFiniteNumber(element?.fontSize, defaults.fontSize ?? 12, {
      min: 1,
    }),
    title:
      safeType === "nutrition"
        ? typeof element?.title === "string" && element.title.trim()
          ? element.title
          : defaults.title
        : element?.title,
    valueHeader:
      safeType === "nutrition"
        ? typeof element?.valueHeader === "string" && element.valueHeader.trim()
          ? element.valueHeader
          : defaults.valueHeader
        : element?.valueHeader,
    nutritionRows,
  };
};

const buildTemplatePayload = (meta, elements) => ({
  name: meta.name.trim(),
  description:
    typeof meta.description === "string" ? meta.description.trim() : "",
  widthMm: toFiniteNumber(meta.widthMm, DEFAULT_TEMPLATE_META.widthMm, {
    min: 1,
  }),
  heightMm: toFiniteNumber(meta.heightMm, DEFAULT_TEMPLATE_META.heightMm, {
    min: 1,
  }),
  paperType: meta.paperType || DEFAULT_TEMPLATE_META.paperType,
  backgroundColor:
    meta.backgroundColor || DEFAULT_TEMPLATE_META.backgroundColor,
  backgroundImageUrl:
    typeof meta.backgroundImageUrl === "string"
      ? meta.backgroundImageUrl.trim()
      : "",
  backgroundImageOpacity: toFiniteNumber(
    meta.backgroundImageOpacity,
    DEFAULT_TEMPLATE_META.backgroundImageOpacity,
    { min: 0, max: 1 },
  ),
  borderColor: meta.borderColor || DEFAULT_TEMPLATE_META.borderColor,
  borderWidth: toFiniteNumber(
    meta.borderWidth,
    DEFAULT_TEMPLATE_META.borderWidth,
    { min: 0 },
  ),
  a4Columns: toFiniteNumber(meta.a4Columns, DEFAULT_TEMPLATE_META.a4Columns, {
    min: 1,
  }),
  a4Rows: toFiniteNumber(meta.a4Rows, DEFAULT_TEMPLATE_META.a4Rows, { min: 1 }),
  a4MarginXMm: toFiniteNumber(
    meta.a4MarginXMm,
    DEFAULT_TEMPLATE_META.a4MarginXMm,
    { min: 0 },
  ),
  a4MarginYMm: toFiniteNumber(
    meta.a4MarginYMm,
    DEFAULT_TEMPLATE_META.a4MarginYMm,
    { min: 0 },
  ),
  a4SpacingXMm: toFiniteNumber(
    meta.a4SpacingXMm,
    DEFAULT_TEMPLATE_META.a4SpacingXMm,
    { min: 0 },
  ),
  a4SpacingYMm: toFiniteNumber(
    meta.a4SpacingYMm,
    DEFAULT_TEMPLATE_META.a4SpacingYMm,
    { min: 0 },
  ),
  elementsJson: JSON.stringify(
    (Array.isArray(elements) ? elements : []).map(sanitizeElement),
  ),
});

const getTemplateSaveErrorMessage = (err) => {
  const message =
    `${err?.response?.data?.message || ""} ${err?.message || ""}`.toLowerCase();
  const trace = err?.response?.data?.trace || "";
  if (trace.includes("elements_json")) {
    return "Template could not be saved. Restart the backend so the template schema migration can run, then try again.";
  }
  if (trace.includes("background_image_url")) {
    return "Template background image could not be saved. Restart the backend so the template schema migration can run, then try again.";
  }
  if (
    message.includes("duplicate") ||
    message.includes("already exists") ||
    message.includes("unique")
  ) {
    return "A template with this name already exists. Choose another name.";
  }
  return err?.response?.data?.message || "Failed to save template";
};

const TemplateDesigner = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const initialConfig = location.state?.newTemplateConfig;

  const [meta, setMeta] = useState({
    name: "",
    description: "",
    widthMm: initialConfig?.widthMm || DEFAULT_TEMPLATE_META.widthMm,
    heightMm: initialConfig?.heightMm || DEFAULT_TEMPLATE_META.heightMm,
    paperType: initialConfig?.paperType || DEFAULT_TEMPLATE_META.paperType,
    backgroundColor: DEFAULT_TEMPLATE_META.backgroundColor,
    backgroundImageUrl: DEFAULT_TEMPLATE_META.backgroundImageUrl,
    backgroundImageOpacity: DEFAULT_TEMPLATE_META.backgroundImageOpacity,
    borderColor: DEFAULT_TEMPLATE_META.borderColor,
    borderWidth: DEFAULT_TEMPLATE_META.borderWidth,
    a4Columns: DEFAULT_TEMPLATE_META.a4Columns,
    a4Rows: DEFAULT_TEMPLATE_META.a4Rows,
    a4MarginXMm: DEFAULT_TEMPLATE_META.a4MarginXMm,
    a4MarginYMm: DEFAULT_TEMPLATE_META.a4MarginYMm,
    a4SpacingXMm: DEFAULT_TEMPLATE_META.a4SpacingXMm,
    a4SpacingYMm: DEFAULT_TEMPLATE_META.a4SpacingYMm,
  });

  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyIndexRef = useRef(0);
  const elements = Array.isArray(history[historyIndex])
    ? history[historyIndex]
    : [];

  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1.5);
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateNameError, setTemplateNameError] = useState("");

  // Real product chosen just for previewing this template with actual data
  const [products, setProducts] = useState([]);
  const [previewProductId, setPreviewProductId] = useState("");
  const [previewProductDataMap, setPreviewProductDataMap] = useState(null);

  const exportNodeRef = useRef(null);

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (!previewProductId) {
      setPreviewProductDataMap(null);
      return;
    }
    resolvePlaceholders({ productId: previewProductId })
      .then(setPreviewProductDataMap)
      .catch(() => setPreviewProductDataMap(null));
  }, [previewProductId]);

  // Prefer the selected product's real data; fall back to generic sample data
  const previewDataMap = previewProductDataMap || SAMPLE_DATA_MAP;

  useEffect(() => {
    if (!isEdit) return;
    getTemplate(id)
      .then((t) => {
        setMeta({
          name: t.name || "",
          description: t.description || "",
          widthMm: t.widthMm || 60,
          heightMm: t.heightMm || 40,
          paperType: t.paperType || "INDIVIDUAL_STICKER",
          backgroundColor: t.backgroundColor || "#ffffff",
          backgroundImageUrl: t.backgroundImageUrl || "",
          backgroundImageOpacity:
            t.backgroundImageOpacity ??
            DEFAULT_TEMPLATE_META.backgroundImageOpacity,
          borderColor: t.borderColor || DEFAULT_TEMPLATE_META.borderColor,
          borderWidth: t.borderWidth ?? DEFAULT_TEMPLATE_META.borderWidth,
          a4Columns: t.a4Columns || 2,
          a4Rows: t.a4Rows || 5,
          a4MarginXMm: t.a4MarginXMm ?? 5,
          a4MarginYMm: t.a4MarginYMm ?? 5,
          a4SpacingXMm: t.a4SpacingXMm ?? 2,
          a4SpacingYMm: t.a4SpacingYMm ?? 2,
        });
        let parsedElements = [];
        try {
          const parsed = t.elementsJson ? JSON.parse(t.elementsJson) : [];
          parsedElements = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          parsedElements = [];
        }
        setHistory([parsedElements]);
        setHistoryIndex(0);
      })
      .catch(() => {
        setHistory([[]]);
        setHistoryIndex(0);
      });
  }, [id, isEdit]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  const pushHistory = useCallback((newElements) => {
    setHistory((prev) => {
      const truncated = prev.slice(0, historyIndexRef.current + 1);
      const next = [...truncated, newElements];
      const nextIndex = next.length - 1;
      historyIndexRef.current = nextIndex;
      setHistoryIndex(nextIndex);
      return next;
    });
  }, []);

  const replaceCurrentElements = useCallback((updater) => {
    setHistory((prev) => {
      const next = [...prev];
      const currentIndex = historyIndexRef.current;
      const currentElements = Array.isArray(next[currentIndex])
        ? next[currentIndex]
        : [];
      next[currentIndex] =
        typeof updater === "function" ? updater(currentElements) : updater;
      return next;
    });
  }, []);

  const beginElementInteraction = useCallback(() => {
    setHistory((prev) => {
      const currentIndex = historyIndexRef.current;
      const currentElements = Array.isArray(prev[currentIndex])
        ? prev[currentIndex]
        : [];
      const truncated = prev.slice(0, currentIndex + 1);
      const draftElements = currentElements.map((item) => ({ ...item }));
      const next = [...truncated, draftElements];
      const nextIndex = next.length - 1;
      historyIndexRef.current = nextIndex;
      setHistoryIndex(nextIndex);
      return next;
    });
  }, []);

  const handleAdd = (type) => {
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 0), 0);
    const newEl = createElement(type, maxZ + 1);
    pushHistory([...elements, newEl]);
    setSelectedId(newEl.id);
  };

  const handleAddDynamicField = (key, label) => {
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 0), 0);
    const newEl = createDynamicFieldElement(key, label, maxZ + 1);
    pushHistory([...elements, newEl]);
    setSelectedId(newEl.id);
  };

  const handleChangeElement = (elId, patch) => {
    pushHistory(elements.map((e) => (e.id === elId ? { ...e, ...patch } : e)));
  };

  const handleChangeElementLive = useCallback(
    (elId, patch) => {
      replaceCurrentElements((currentElements) =>
        currentElements.map((e) => (e.id === elId ? { ...e, ...patch } : e)),
      );
    },
    [replaceCurrentElements],
  );

  const hasDuplicateTemplateName = useCallback(
    async (name) => {
      const normalizedName = name.trim().toLowerCase();
      if (!normalizedName) return false;

      const templates = await getTemplates();
      return templates.some((template) => {
        const templateName =
          typeof template?.name === "string"
            ? template.name.trim().toLowerCase()
            : "";
        return (
          templateName === normalizedName &&
          String(template.id) !== String(id || "")
        );
      });
    },
    [id],
  );

  const handleDuplicate = (elId) => {
    const el = elements.find((e) => e.id === elId);
    if (!el) return;
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 0), 0);
    const copy = {
      ...el,
      id: `${el.id}_copy_${Date.now()}`,
      x: el.x + 3,
      y: el.y + 3,
      zIndex: maxZ + 1,
    };
    pushHistory([...elements, copy]);
    setSelectedId(copy.id);
  };

  const handleDelete = (elId) => {
    pushHistory(elements.filter((e) => e.id !== elId));
    setSelectedId(null);
  };

  const handleBringForward = (elId) => {
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 0), 0);
    handleChangeElement(elId, { zIndex: maxZ + 1 });
  };

  const handleSendBackward = (elId) => {
    const minZ = elements.reduce((m, e) => Math.min(m, e.zIndex || 0), 0);
    handleChangeElement(elId, { zIndex: minZ - 1 });
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  const handleUndo = () => canUndo && setHistoryIndex((i) => i - 1);
  const handleRedo = () => canRedo && setHistoryIndex((i) => i + 1);

  const selectedElement = elements.find((e) => e.id === selectedId) || null;
  const selectedBackgroundTheme = getThemeSelectionValue(
    meta.backgroundImageUrl,
  );

  const handleBackgroundThemeChange = (themeKey) => {
    if (themeKey === NO_BACKGROUND_OPTION) {
      setMeta({ ...meta, backgroundImageUrl: "" });
      return;
    }

    if (themeKey === CUSTOM_BACKGROUND_OPTION) {
      setMeta({ ...meta, backgroundImageUrl: meta.backgroundImageUrl || "" });
      return;
    }

    const preset = findThemeByKey(themeKey);
    if (preset) {
      setMeta({ ...meta, backgroundImageUrl: preset.url });
    }
  };

  const handleSave = async () => {
    const payload = buildTemplatePayload(meta, elements);
    setTemplateNameError("");

    if (!payload.name) {
      setTemplateNameError("Template name is required.");
      toast.error("Template name is required");
      return;
    }
    if (payload.widthMm <= 0 || payload.heightMm <= 0) {
      toast.error("Width and height must be greater than 0");
      return;
    }

    try {
      const duplicateName = await hasDuplicateTemplateName(payload.name);
      if (duplicateName) {
        setTemplateNameError(
          "A template with this name already exists. Choose another name.",
        );
        toast.error(
          "A template with this name already exists. Choose another name.",
        );
        return;
      }
    } catch (err) {
      // Fall back to backend validation if the pre-check is unavailable.
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateTemplate(id, payload);
        toast.success("Template updated");
      } else {
        await createTemplate(payload);
        toast.success("Template created");
      }
      navigate("/label-studio/templates");
    } catch (err) {
      const saveMessage = getTemplateSaveErrorMessage(err);
      if (saveMessage.includes("already exists")) {
        setTemplateNameError(saveMessage);
      }
      toast.error(saveMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    if (!exportNodeRef.current) return;
    try {
      await exportSingleLabelPdf(
        exportNodeRef.current,
        meta.widthMm,
        meta.heightMm,
        `${meta.name || "label"}.pdf`,
      );
    } catch (err) {
      toast.error("Failed to export PDF");
    }
  };

  const handlePrint = async () => {
    if (!exportNodeRef.current) return;
    try {
      await printLabelNode(exportNodeRef.current, meta.widthMm, meta.heightMm);
    } catch (err) {
      toast.error("Failed to print");
    }
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Template Name"
              size="small"
              fullWidth
              error={Boolean(templateNameError)}
              helperText={templateNameError || " "}
              value={meta.name}
              onChange={(e) => {
                setTemplateNameError("");
                setMeta({ ...meta, name: e.target.value });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Paper Type"
              size="small"
              fullWidth
              value={meta.paperType}
              onChange={(e) => setMeta({ ...meta, paperType: e.target.value })}
            >
              {PAPER_TYPES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Width (mm)"
              size="small"
              type="number"
              fullWidth
              value={meta.widthMm}
              onChange={(e) =>
                setMeta({ ...meta, widthMm: Number(e.target.value) })
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Height (mm)"
              size="small"
              type="number"
              fullWidth
              value={meta.heightMm}
              onChange={(e) =>
                setMeta({ ...meta, heightMm: Number(e.target.value) })
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Background"
              size="small"
              type="color"
              fullWidth
              value={meta.backgroundColor}
              onChange={(e) =>
                setMeta({ ...meta, backgroundColor: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Background Theme"
              size="small"
              fullWidth
              value={selectedBackgroundTheme}
              onChange={(e) => handleBackgroundThemeChange(e.target.value)}
            >
              <MenuItem value={NO_BACKGROUND_OPTION}>Solid Color Only</MenuItem>
              {LABEL_BACKGROUND_THEMES.map((theme) => (
                <MenuItem key={theme.key} value={theme.key}>
                  {theme.label}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_BACKGROUND_OPTION}>
                Custom Image URL
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Background Image URL"
              size="small"
              fullWidth
              value={meta.backgroundImageUrl}
              placeholder="https://... or /images/theme.png"
              helperText="Use a same-origin or CORS-enabled image for PDF/print export."
              onChange={(e) =>
                setMeta({ ...meta, backgroundImageUrl: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Image Opacity"
              size="small"
              type="number"
              fullWidth
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              value={meta.backgroundImageOpacity}
              onChange={(e) =>
                setMeta({
                  ...meta,
                  backgroundImageOpacity: Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Paper Border"
              size="small"
              type="color"
              fullWidth
              value={meta.borderColor}
              onChange={(e) =>
                setMeta({ ...meta, borderColor: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              label="Border Width"
              size="small"
              type="number"
              fullWidth
              value={meta.borderWidth}
              onChange={(e) =>
                setMeta({ ...meta, borderWidth: Number(e.target.value) })
              }
            />
          </Grid>
          {meta.paperType === "A4_SHEET" && (
            <>
              <Grid item xs={6} md={1.5}>
                <TextField
                  label="Columns"
                  size="small"
                  type="number"
                  fullWidth
                  value={meta.a4Columns}
                  onChange={(e) =>
                    setMeta({ ...meta, a4Columns: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <TextField
                  label="Rows"
                  size="small"
                  type="number"
                  fullWidth
                  value={meta.a4Rows}
                  onChange={(e) =>
                    setMeta({ ...meta, a4Rows: Number(e.target.value) })
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TopToolbar
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          zoom={zoom}
          onZoomChange={setZoom}
          previewMode={previewMode}
          onTogglePreview={() => setPreviewMode((p) => !p)}
          products={products}
          previewProductId={previewProductId}
          onPreviewProductChange={setPreviewProductId}
          onSave={handleSave}
          onExportPdf={handleExportPdf}
          onPrint={handlePrint}
          saving={saving}
        />

        <Box sx={{ display: "flex", height: "70vh" }}>
          <LeftPanel
            onAdd={handleAdd}
            onAddDynamicField={handleAddDynamicField}
          />

          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              bgcolor: "#e9ecef",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              p: 4,
            }}
            onClick={() => setSelectedId(null)}
          >
            <Box
              className="label-canvas-root"
              sx={{
                position: "relative",
                backgroundColor: meta.backgroundColor,
                width: mmToPx(meta.widthMm) * zoom,
                height: mmToPx(meta.heightMm) * zoom,
                border: meta.borderWidth
                  ? `${meta.borderWidth}px solid ${meta.borderColor}`
                  : "none",
                boxSizing: "border-box",
                boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
              }}
            >
              {meta.backgroundImageUrl ? (
                <Box
                  component="img"
                  alt=""
                  src={meta.backgroundImageUrl}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: meta.backgroundImageOpacity,
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              ) : null}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  width: mmToPx(meta.widthMm),
                  height: mmToPx(meta.heightMm),
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                }}
              >
                {elements
                  .filter((e) => !e.hidden)
                  .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                  .map((el) => (
                    <DesignerCanvasElement
                      key={el.id}
                      element={el}
                      isSelected={el.id === selectedId}
                      onSelect={setSelectedId}
                      onChange={handleChangeElementLive}
                      onCommitChange={handleChangeElement}
                      onInteractionStart={beginElementInteraction}
                      dataMap={previewMode ? previewDataMap : null}
                      zoom={zoom}
                      canvasWidthMm={meta.widthMm}
                      canvasHeightMm={meta.heightMm}
                    />
                  ))}
              </Box>
            </Box>
          </Box>

          <RightPanel
            element={selectedElement}
            onChange={handleChangeElement}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onBringForward={handleBringForward}
            onSendBackward={handleSendBackward}
          />
        </Box>
      </Paper>

      {/* Hidden, clean render used as the source for PDF export / print (no selection outlines) */}
      <Box sx={{ position: "fixed", left: -99999, top: 0 }}>
        <div ref={exportNodeRef}>
          <LabelCanvas
            widthMm={meta.widthMm}
            heightMm={meta.heightMm}
            backgroundColor={meta.backgroundColor}
            backgroundImageUrl={meta.backgroundImageUrl}
            backgroundImageOpacity={meta.backgroundImageOpacity}
            borderColor={meta.borderColor}
            borderWidth={meta.borderWidth}
            elements={elements}
            dataMap={previewMode ? previewDataMap : null}
          />
        </div>
      </Box>
    </Box>
  );
};

export default TemplateDesigner;
